import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { configurePurchases, purchasesLogOut } from "@/lib/purchases";

// Suppress AbortErrors globally — these are thrown when Android kills a network
// request mid-flight (e.g. phone goes to sleep, app backgrounds). They are
// harmless and expected in a native WebView environment.
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        const isAbort =
            event.reason?.name === 'AbortError' ||
            event.reason?.message?.includes('aborted') ||
            event.reason?.code === 20;
        if (isAbort) event.preventDefault();
    });
}

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [isSetupComplete, setIsSetupComplete] = useState(false);
    const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
    // profileChecked becomes true once a profile fetch attempt has resolved,
    // so ProtectedRoute knows it's safe to evaluate onboarding status.
    const [profileChecked, setProfileChecked] = useState(false);

    const checkSetupComplete = useCallback(async (userId) => {
        try {
            // Fast cache check first
            const localSetupState = localStorage.getItem(`homeezi_setup_${userId}`);
            if (localSetupState === "true") {
                setIsSetupComplete(true);
            }

            const { data: profileData, error } = await supabase
                .from('profiles')
                .select('id, full_name, is_onboarded, is_admin, notification_preferences, subscription_plan, max_properties, has_premium_features, guide_version, trial_expires_at, has_used_trial')
                .eq('id', userId)
                .maybeSingle();

            if (error) throw error;

            setUserProfile(profileData);
            setIsAdmin(!!profileData?.is_admin);
            
            // Initialize RevenueCat with user ID
            configurePurchases(userId);
            
            // Determine onboarding status
            // If they have no profile, they are NOT onboarded.
            let isDone = profileData ? !!profileData.is_onboarded : false;
            
            // If they are marked as done locally, force the DB to catch up or ignore it
            if (localSetupState === "true") isDone = true;
            
            // Cache successful true state
            if (isDone) {
                localStorage.setItem(`homeezi_setup_${userId}`, "true");
            }

            setIsSetupComplete(isDone);
            return profileData;
        } catch (err) {
            console.error("Profile check failed:", err);
            // On error, default to complete so the user isn't stuck in setup loop
            setIsSetupComplete(true);
            return null;
        } finally {
            setProfileChecked(true);
        }
    }, []);

    useEffect(() => {
        let initialized = false;
        let currentUserId = null;

        // 1. Manually fetch the session immediately to guarantee we don't wait forever
        const initializeAuth = async () => {
            try {
                const { data: { session: initialSession } } = await supabase.auth.getSession();
                
                if (!initialized) {
                    const initialUser = initialSession?.user ?? null;
                    setSession(initialSession);
                    setUser(initialUser);
                    currentUserId = initialUser?.id ?? null;

                    if (initialUser) {
                        // Do NOT await this! Let it fetch in the background
                        checkSetupComplete(initialUser.id);
                    } else {
                        setIsAdmin(false);
                        setIsSetupComplete(false);
                        setUserProfile(null);
                        setProfileChecked(true);
                    }

                    // Drop loading screen instantly!
                    setLoading(false);
                    initialized = true;
                }
            } catch (err) {
                // AbortErrors are expected when Android interrupts network requests
                // (phone sleep, app backgrounded). Silently ignore them.
                if (err?.name === 'AbortError' || err?.code === 20) return;
                console.error("Auth init error:", err);
                if (!initialized) setLoading(false);
            }
        };

        initializeAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, newSession) => {
                if (event === 'INITIAL_SESSION' && initialized) return;

                if (event === 'PASSWORD_RECOVERY') {
                    setIsPasswordRecovery(true);
                }

                // Handle expired/invalid session — token could not be refreshed.
                // This happens when a user leaves the app open for many days.
                // Without this, they'd just get silent 401 errors from every API call.
                if (event === 'TOKEN_REFRESH_FAILED') {
                    setSession(null);
                    setUser(null);
                    setIsAdmin(false);
                    setIsSetupComplete(false);
                    setUserProfile(null);
                    // Import toast dynamically to avoid circular dependency
                    import('@/hooks/use-toast').then(({ toast }) => {
                        toast({
                            title: "Session Expired",
                            description: "Your session has expired. Please sign in again.",
                            variant: "destructive",
                        });
                    });
                    // Redirect to login — use replace so back button doesn't loop
                    setTimeout(() => {
                        window.location.replace('/auth');
                    }, 1500);
                    return;
                }

                const newUser = newSession?.user ?? null;
                const isSameUser = newUser && currentUserId === newUser.id;
                currentUserId = newUser?.id ?? null;

                setSession(newSession);
                setUser(newUser);

                if (newUser) {
                    if (!isSameUser) {
                        setProfileChecked(false);
                    }
                    // Do NOT await this! Let it fetch in the background
                    checkSetupComplete(newUser.id);
                } else {
                    setIsAdmin(false);
                    setIsSetupComplete(false);
                    setUserProfile(null);
                    setProfileChecked(true);
                    purchasesLogOut();
                }

                if (!initialized) {
                    setLoading(false);
                    initialized = true;
                }
            }
        );

        // Safety net: force-clear loading after 4s if Supabase never responds
        const safetyTimer = setTimeout(() => {
            if (!initialized) {
                console.warn("Auth initialization safety timeout reached.");
                setProfileChecked(true);
                setLoading(false);
            }
        }, 4000);

        return () => {
            subscription.unsubscribe();
            clearTimeout(safetyTimer);
        };
    }, [checkSetupComplete]);

    useEffect(() => {
        const handleRefetch = () => {
            if (user?.id) checkSetupComplete(user.id);
        };
        window.addEventListener('refetch-profile', handleRefetch);

        return () => {
            window.removeEventListener('refetch-profile', handleRefetch);
        };
    }, [user, checkSetupComplete]);

    const signUp = async (email, password, fullName = "") => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: { 
                emailRedirectTo: `${window.location.origin}/app`,
                data: { full_name: fullName }
            },
        });
        return { data, error };
    };

    const resendVerification = async (email) => {
        const { error } = await supabase.auth.resend({
            type: 'signup',
            email,
            options: { emailRedirectTo: `${window.location.origin}/app` }
        });
        return { error };
    };

    const signIn = async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error };
    };

    const signOut = async () => {
        await supabase.auth.signOut();
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                userProfile,
                session,
                loading,
                profileChecked,
                isAdmin,
                isSetupComplete,
                isPasswordRecovery,
                setIsPasswordRecovery,
                signUp,
                signIn,
                signOut,
                checkSetupComplete,
                resendVerification,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
